import { useTranslation } from "react-i18next";
import { SearchNormal1, Calendar2, Status } from "iconsax-react";
import { Select, Input, DatePicker } from "@/shared/ui";
import { FilterResetButton, ViewModeToggle } from "@/shared/components";
import { type Locale } from "@/shared/utils";
import type { ApplicationStatus } from "@/features/hr/hiring";

export interface StatusOption {
  label: string;
  value: ApplicationStatus | null;
}

interface Props {
  search: string;
  onSearchChange: (v: string) => void;
  range: { start: Date | null; end: Date | null };
  onRangeChange: (r: { start: Date | null; end: Date | null }) => void;
  statusFilter: ApplicationStatus | null;
  onStatusChange: (v: ApplicationStatus | null) => void;
  statusOptions: StatusOption[];
  hasActiveFilters: boolean;
  onReset: () => void;
  locale: Locale;
  view: "table" | "cards";
  onViewChange: (v: "table" | "cards") => void;
}

export default function HiringFilter({
  search,
  onSearchChange,
  range,
  onRangeChange,
  statusFilter,
  onStatusChange,
  statusOptions,
  hasActiveFilters,
  onReset,
  locale,
  view,
  onViewChange,
}: Props) {
  const { t } = useTranslation("HiringPage");

  function handleDateRangeChange(v: Date | { start: Date | null; end: Date | null } | null) {
    if (!v || v instanceof Date) return;
    onRangeChange(v);
  }

  return (
    <div className="flex items-center justify-between gap-2 mb-7">
      <div className="flex items-center gap-2">
        <div className="min-w-48">
          <Input
            isSmall
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("filters.searchPlaceholder")}
            icon={<SearchNormal1 size={14} color="currentColor" />}
          />
        </div>

        <DatePicker
          value={range}
          mode="range"
          onChange={handleDateRangeChange}
          locale={locale}
          placeholder={t("filters.invitationDate")}
          isSmall
          width="min-w-40"
          icon={<Calendar2 size={16} color="currentColor" />}
          align="left"
        />

        <div className="min-w-40">
          <Select<ApplicationStatus | null>
            options={statusOptions}
            placeholder={t("filters.allStatuses")}
            value={statusFilter}
            onChange={onStatusChange}
            isSmall
            icon={<Status size={16} color="currentColor" />}
            width="w-56"
          />
        </div>

        {hasActiveFilters && (
          <FilterResetButton label={t("filters.reset")} onClick={onReset} />
        )}
      </div>

      <div className="flex items-center gap-3">
        <ViewModeToggle
          value={view}
          onChange={onViewChange}
          tableLabel={t("view.table")}
          cardsLabel={t("view.cards")}
        />
      </div>
    </div>
  );
}
